import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { debugLog } from "@/lib/debug";
import { Sparkles, Loader2 } from "lucide-react";

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function ReviewOutput({ review }) {
  if (!review) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="ai-review-output">
      <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-2">
        AI Review · {review.property} · {review.count} work orders analyzed
      </div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{review.review}</div>
    </div>
  );
}

function ChatSection({ center, question, setQuestion, asking, history, onAsk }) {
  if (!center) return null;
  return (
    <>
      {history.length > 0 && (
        <div className="space-y-3 pt-3 border-t border-slate-100">
          {history.map((h, i) => (
            <div key={`${i}-${h.q.slice(0, 16)}`} className="space-y-1" data-testid={`ai-chat-${i}`}>
              <div className="text-xs font-mono text-slate-500">Q: {h.q}</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap bg-blue-50 border border-blue-100 rounded-md p-3">{h.a}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          className="bg-white"
          placeholder={`Ask AI a question about ${center} issues...`}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAsk(); } }}
          data-testid="ai-question-input"
        />
        <Button onClick={onAsk} disabled={asking || !question.trim()} className="bg-[#478261] hover:bg-[#3a6b51] text-white shrink-0" data-testid="ai-ask-button">
          {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}
        </Button>
      </div>
    </>
  );
}

export default function AiCenterReview({ api }) {
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState("");
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${api}/meta`);
        setCenters(["ALL CENTERS", ...(m.data.properties || [])]);
      } catch (e) {
        debugLog("meta load failed:", e);
        toast.error("Failed to load centers");
      }
    })();
  }, [api]);

  const pickCenter = (v) => { setCenter(v); setReview(null); setChatHistory([]); };

  const runReview = async () => {
    if (!center) { toast.error("Select a center first"); return; }
    setReviewing(true);
    setReview(null);
    try {
      const r = await axios.get(`${api}/ai/center-review`, { params: { property: center } });
      setReview(r.data);
    } catch (e) {
      toast.error(errorDetail(e, "AI review failed"));
    } finally {
      setReviewing(false);
    }
  };

  const askQuestion = async () => {
    const q = question.trim();
    if (!q) return;
    if (!center) { toast.error("Select a center first"); return; }
    setAsking(true);
    try {
      const r = await axios.post(`${api}/ai/center-chat`, { property: center, question: q, history: chatHistory });
      setChatHistory((h) => [...h, { q, a: r.data.answer }]);
      setQuestion("");
    } catch (e) {
      toast.error(errorDetail(e, "AI answer failed"));
    } finally {
      setAsking(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="ai-center-review-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <CardTitle className="text-lg">AI Center Review</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <p className="text-sm text-slate-600">Pick ALL CENTERS or an individual center — AI reviews its work orders (including updates and completion notes) to surface recurring patterns, what worked vs. what didn't, and proactive solutions.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={center} onValueChange={pickCenter}>
            <SelectTrigger className="bg-white sm:w-[280px]" data-testid="ai-center-select">
              <SelectValue placeholder="Select a center" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {centers.map((c) => (<SelectItem key={c} value={c} data-testid={`ai-center-option-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button onClick={runReview} disabled={reviewing || !center} className="bg-[#478261] hover:bg-[#3a6b51] text-white shrink-0" data-testid="ai-review-button">
            {reviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {reviewing ? "Analyzing..." : "Run AI Review"}
          </Button>
        </div>
        <ReviewOutput review={review} />
        <ChatSection center={center} question={question} setQuestion={setQuestion} asking={asking} history={chatHistory} onAsk={askQuestion} />
      </CardContent>
    </Card>
  );
}
